'use client';

import { useEffect, useRef, useState } from 'react';

interface UseViewportActivationOptions {
    rootMargin?: string;
    threshold?: number;
    /** 初始激活状态，对首屏组件设为 true 可避免首次渲染闪烁 */
    initialActive?: boolean;
}

function useViewportActivation<T extends Element>({
    rootMargin = '300px 0px',
    threshold = 0,
    initialActive = false,
}: UseViewportActivationOptions = {}) {
    const ref = useRef<T | null>(null);
    const [isIntersecting, setIsIntersecting] = useState(initialActive);
    const [isDocumentVisible, setIsDocumentVisible] = useState(true);

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsDocumentVisible(!document.hidden);
        };

        handleVisibilityChange();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, []);

    useEffect(() => {
        const target = ref.current;
        if (!target) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsIntersecting(entry.isIntersecting);
            },
            {
                rootMargin,
                threshold,
            },
        );

        observer.observe(target);

        return () => {
            observer.disconnect();
        };
    }, [rootMargin, threshold]);

    return {
        ref,
        isActive: isIntersecting && isDocumentVisible,
        isIntersecting,
        isDocumentVisible,
    };
}

export default useViewportActivation;
